// EnterVerificationCodeComponent.tsx
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "../useTranslation";

type Language = "ja" | "en";

const translations = {
  ja: {
    smsVerificationCode: "SMS認証コード: ",
    verifyPlaceholder: "認証コード",
    verifyCode: "認証",
    poweredBy: "Powered by IIC",
    iicHome: "IIC Home",
    termsOfUse: "利用規約",
    privacyPolicy: "プライバシーポリシー",
    tokenExpired: "トークンの有効期限が切れているか、以前のコードがあります。再度ログインしてください。",
    internalError: "内部エラーが発生しました。",
    errorRequestingCode: "認証コードのリクエストエラー: ",
    tryLoggingInAgain: "再度ログインしてください。",
    iicWebURL: "https://www.iic-hq.co.jp/",
    sitepolicyWebURL: "https://www.ihi.co.jp/sitepolicy/",
    privacyWebURL: "https://www.ihi.co.jp/privacy/",
  },
  en: {
    smsVerificationCode: "SMS Verification Code: ",
    verifyPlaceholder: "Verify",
    verifyCode: "Verify Code",
    poweredBy: "Powered by IIC",
    iicHome: "IIC Home",
    termsOfUse: "Terms of Use",
    privacyPolicy: "Privacy Policy",
    tokenExpired: "Token has expired, or you have a previous code. Please re-login.",
    internalError: "An internal error has occurred.",
    errorRequestingCode: "Error requesting verification code: ",
    tryLoggingInAgain: "Try logging in again.",
    iicWebURL: "https://www.iic-hq.co.jp/en/",
    sitepolicyWebURL: "https://www.ihi.co.jp/en/sitepolicy/",
    privacyWebURL: "https://www.ihi.co.jp/en/privacy/",
  },
};

function getLanguageFromURL(): Language {
  const params = new URLSearchParams(window.location.search);
  const lang = params.get("lang");
  return lang === "en" ? "en" : "ja";
}

// Translate server/client error messages based on lang
const translateError = (lang: "ja" | "en", msg: string) => {
  if (lang === "en") {
    const map: { [k: string]: string } = {
      "トークンの有効期限が切れました。再度ログインしてください。":
        "Token has expired. Please re-login.",
      "トークンの有効期限が切れているか、以前のコードが残っています。再度ログインしてください。":
        "Token has expired, or you have a previous code. Please re-login.",
      "内部エラーが発生しました。": "An internal error has occurred.",
      "認証コードのリクエストエラー: ":
        "Error requesting verification code: ",
      "データの取得中にエラーが発生しました。":
        "An error occurred while retrieving the data.",
      "認証されていません。": "Unauthorized.",
      "電話番号が無効です。再度確認してください。":
        "Phone number is invalid. Please check again.",
      "電話番号は有効な国からである必要があります。":
        "Phone number must be from a valid country.",
    };
    // Exact match
    if (map[msg]) return map[msg];
    // Prefix match (for requestError concatenated messages)
    for (const [jp, en] of Object.entries(map)) {
      if (msg.startsWith(jp)) {
        return en + msg.slice(jp.length);
      }
    }
  }
  return msg;
};

function EnterVerificationCodeComponent({ setStep, phoneNumber }: any) {
  const [verificationCode, setVerificationCode] = useState("");
  const [error, setError] = useState<string | null>(null); // Add this state variable to hold an error message
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const [isClicked, setIsClicked] = useState(false);
  const [isCodeRequested, setIsCodeRequested] = useState(false); // State to track if the code request has been made
  const [language, setLanguage] = useState<Language>(getLanguageFromURL());

  const apiUrl =
    process.env.REACT_APP_EXPRESS_API_URL || "http://localhost:3001";

  // Check language parameter on mount and when URL changes
  useEffect(() => {
    const checkLanguage = () => {
      setLanguage(getLanguageFromURL());
    };
    // Check immediately on mount/reload
    checkLanguage();
    // Listen for popstate events (back/forward navigation)
    window.addEventListener("popstate", checkLanguage);
    return () => {
      window.removeEventListener("popstate", checkLanguage);
    };
  }, []);

  const t = translations[language];

  useEffect(() => {
    const handleKeyDown = (e: { key: string }) => {
      if (e.key === "Enter" && buttonRef.current) {
        // Check if the button is visible
        if (window.getComputedStyle(buttonRef.current).display !== "none") {
          buttonRef.current.click();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    // Cleanup listener when component unmounts
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // if phone field is empty, send the verification code to the phone in the db
  const requestVerificationCode = () => {
    if (isCodeRequested) return; // Early return if code has already been requested or retries exhausted
    console.log("Requesting verification code ...");
    setIsCodeRequested(true); // Set flag to true to avoid further requests
    if (!phoneNumber) {
      axios
        .post(
          `${apiUrl}/verify`,
          {},
          {
            headers: {
              authorization: `Bearer ${sessionStorage.getItem("token")}`,
            },
          }
        )
        .then((res) => {
          // Handle the response here, if necessary
          if (res.status === 401) {
            throw new Error(t.tokenExpired);
          } else if (res.status === 500) {
            throw new Error(t.internalError);
          }
        })
        .catch((error) => {
          const msg = error.response?.data?.status
            ? error.response?.data?.status
            : error.message;
          const new_msg = `${t.errorRequestingCode}${msg}. ${t.tryLoggingInAgain}`;
          setError(translateError(language, new_msg));
          setIsCodeRequested(true); // Allow further requests after an error
        });
    }
  };

  useEffect(() => {
    if (!phoneNumber && !isCodeRequested) {
      requestVerificationCode();
    }
  }); // Depend on phoneNumber and isCodeRequested to manage re-fetching logic

  const handleVerificationCodeSubmit = () => {
    if (isClicked) return; // Prevent multiple clicks. TODO: reset after X seconds
    setIsClicked(true); // disable the button

    let body = {};
    if (phoneNumber) {
      // brand new verification
      body = { code: verificationCode, phone: phoneNumber };
    } else {
      body = { code: verificationCode };
    }

    axios
      .post(`${apiUrl}/check`, body, {
        headers: {
          authorization: `Bearer ${sessionStorage.getItem("token")}`,
        },
      })
      .then((res) => {
        if (res.status === 401) {
          throw new Error(t.tokenExpired);
        } else if (res.status === 500) {
          throw new Error(t.internalError);
        }

        // Now that 2fa has come back, we can fill in the right token to enable wasm
        // Modifying a form with a token is vulnerable to XSS (and was this way before I joined),
        // Ideally we'll fix the wasm to check cookies.
        // If account/site is 2fa enabled, skipping to this step should result in a token
        // that won't work
        var legacy_element = document.getElementById(
          "elementId"
        ) as HTMLInputElement;
        if (!legacy_element) {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = "token";
          input.id = "token";
          input.value = res.data.token;
          document.body.appendChild(input);
        } else {
          legacy_element.value = res.data.token;
        }
        setStep("verified");
      })
      .catch((error) => {
        let msg = error.response.data.status || error.message;
        setError(translateError(language, msg));
        setIsClicked(false);
      });
  };

  return (
    <div className="center">
      <img className="logo" src="images/Dap3_logo.png" alt="IIC's Logo"></img>
      <form onSubmit={(e) => e.preventDefault()}>
        <table align="center" style={{ width: "80%" }}>
          <tbody>
            <tr>
              <td className="dlabel" style={{fontFamily:'"Meiryo UI", Meiryo, Arial, sans-serif'}}>{t.smsVerificationCode}</td>
              <td>
                <input
                  id="verify"
                  type="text"
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder={t.verifyPlaceholder}
                  className="input"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </form>
      <br />
      <div
        className="dlabel"
        id="login_feedback"
        style={{ color: "#ff0000", display: "block", fontFamily:'"Meiryo UI", Meiryo, Arial, sans-serif' }}
      >
        &nbsp; {error} {/* Display the error message if there is one */}
      </div>
      <br />
      <button
        ref={buttonRef}
        onClick={handleVerificationCodeSubmit}
        className="login_button"
        value={t.verifyCode}
        disabled={isClicked}
        style={{ backgroundColor: isClicked ? "gray" : "", fontFamily:'"Meiryo UI", Meiryo, Arial, sans-serif' }}
      >
        {t.verifyCode}
      </button>
      <div
        className="dlabel"
        style={{ margin: "0 auto", display: "block", paddingTop: "10%" }}
      >
        {" "}
        {t.poweredBy}
      </div>
      <div
        className="dlabel"
        style={{
          fontWeight: "normal",
          padding: "3%",
          paddingTop: "2%",
          margin: "0 auto",
          display: "block",
        }}
      >
        <a href={t.iicWebURL} target="_blank" rel= "noopener noreferrer" style={{ color: "white", fontFamily:'"Meiryo UI", Meiryo, Arial, sans-serif' }}>
          {t.iicHome}
        </a>{" "}
        |{" "}
        <a href={t.sitepolicyWebURL} target="_blank" rel= "noopener noreferrer" style={{ color: "white", fontFamily:'"Meiryo UI", Meiryo, Arial, sans-serif' }}>
          {t.termsOfUse}
        </a>{" "}
        |{" "}
        <a href={t.privacyWebURL} target="_blank" rel= "noopener noreferrer" style={{ color: "white", fontFamily:'"Meiryo UI", Meiryo, Arial, sans-serif' }}>
          {t.privacyPolicy}
        </a>
      </div>
    </div>
  );
}

export default EnterVerificationCodeComponent;
